/**
 * Header Transparency & Sticky Header System
 * Extracted from inline script for better performance
 * Only active on desktop (1024px+)
 */

// Header transparency initialization function
function initHeaderTransparency() {
  const isDesktop = window.matchMedia('(min-width: 1024px)').matches;
  const body = document.body;

  // Clean up existing transparency system
  if (window.HeaderTransparency) {
    // Remove existing classes and attributes
    body.removeAttribute('data-header-active');
    body.removeAttribute('data-header-hovered');

    const headerGroup = document.querySelector('#header-group');
    if (headerGroup) {
      headerGroup.classList.remove('header-transparent');
    }
  }

  // Only enable header transparency on desktop devices (1024px+)
  if (!isDesktop) {
    window.HeaderTransparency = null;
    return; // Exit early on mobile/tablet
  }

  // Initialize header active state tracking (desktop only)
  body.setAttribute('data-header-active', 'false');

  // Global utility functions for header transparency (desktop only)
  window.HeaderTransparency = {
    // Set header as active (prevents transparency)
    setActive: function() {
      body.setAttribute('data-header-active', 'true');
      this.updateTransparency();
    },

    // Set header as inactive (allows transparency)
    setInactive: function() {
      body.setAttribute('data-header-active', 'false');
      this.updateTransparency();
    },

    // Check if header is currently active
    isActive: function() {
      return body.getAttribute('data-header-active') === 'true';
    },

    // Update header transparency based on current state
    updateTransparency: function() {
      // Only do transparency logic on index page
      const isIndexPage = document.body.classList.contains('index-page');
      if (!isIndexPage) return;

      const headerGroup = document.querySelector('#header-group');
      if (!headerGroup) return;

      const isHeaderActive = this.isActive();
      const isHovered = body.hasAttribute('data-header-hovered');

      // Only make header transparent if:
      // 1. We're at the top of the page (scrollY <= 100)
      // 2. Header is not active (no dropdowns, menus, search, etc.)
      // 3. Mouse is not over the header
      if (window.scrollY <= 100 && !isHeaderActive && !isHovered) {
        headerGroup.classList.add('header-transparent');
      } else {
        headerGroup.classList.remove('header-transparent');
      }
    }
  };

  // Index-page specific functionality
  const isIndexPage = document.body.classList.contains('index-page');
  if (isIndexPage) {
    const headerGroup = document.querySelector('#header-group');
    const siteHeader = document.querySelector('.site-header');

    // Header mouse events - separate from other active states
    if (siteHeader && headerGroup) {
      siteHeader.addEventListener('mouseenter', () => {
        body.setAttribute('data-header-hovered', 'true');
        headerGroup.classList.remove('header-transparent');
      });

      siteHeader.addEventListener('mouseleave', () => {
        body.removeAttribute('data-header-hovered');
        if (window.HeaderTransparency) {
          window.HeaderTransparency.updateTransparency();
        }
      });
    }

    // Scroll events (transparency - index page specific)
    document.addEventListener('scroll', () => {
      const scrollY = window.scrollY;

      // Handle transparency (note: global sticky handler handles stuck state)
      if (scrollY > 100 && headerGroup) {
        headerGroup.classList.remove('header-transparent');
      } else if (window.HeaderTransparency) {
        window.HeaderTransparency.updateTransparency();
      }
    });
  }

  // Auto-detect existing mega-menu dropdowns (available on all pages)
  let megaMenuWasActive = false; // Track if mega-menus were the cause of active state

  document.querySelectorAll('.header-menu-item').forEach((item) => {
    // Watch for dropdown state changes
    const observer = new MutationObserver(() => {
      // Check if ANY mega-menu is open
      const hasOpenMenus = document.querySelectorAll('.header-menu-item[open]').length > 0;

      if (hasOpenMenus && !megaMenuWasActive) {
        // Mega-menus just opened - set active
        megaMenuWasActive = true;
        if (window.HeaderTransparency) {
          window.HeaderTransparency.setActive();
        }
      } else if (!hasOpenMenus && megaMenuWasActive) {
        // Mega-menus just closed and they were the reason for active state
        megaMenuWasActive = false;
        if (window.HeaderTransparency) {
          window.HeaderTransparency.setInactive();
        }
      }
      // If mega-menus open/close but they weren't the reason for active state, do nothing
    });

    observer.observe(item, {
      attributes: true,
      attributeFilter: ['open']
    });
  });

  // Unified event listeners for header transparency
  document.addEventListener('header:setActive', () => {
    if (window.HeaderTransparency) {
      window.HeaderTransparency.setActive();
    }
  });
  document.addEventListener('header:setInactive', () => {
    if (window.HeaderTransparency) {
      window.HeaderTransparency.setInactive();
    }
  });

  // Global scroll handler for sticky header (works on all pages)
  let stickyScrollTimeout;
  document.addEventListener('scroll', () => {
    // Throttle scroll events to prevent excessive updates
    clearTimeout(stickyScrollTimeout);
    stickyScrollTimeout = setTimeout(() => {
      const scrollY = window.scrollY;

      // Handle sticky header with hysteresis to prevent flickering
      if (window.HeaderSticky && window.HeaderSticky.isEnabled()) {
        window.HeaderSticky.handleScroll(scrollY);
      }
    }, 5); // Small delay to throttle
  });

  // Watch for header state attribute changes
  const headerStateObserver = new MutationObserver((mutations) => {
    mutations.forEach((mutation) => {
      if (mutation.type === 'attributes' && mutation.attributeName === 'data-header-active') {
        if (window.HeaderTransparency) {
          window.HeaderTransparency.updateTransparency();
        }
      }
    });
  });

  headerStateObserver.observe(body, {
    attributes: true,
    attributeFilter: ['data-header-active']
  });
}

// Initialize on page load
document.addEventListener('DOMContentLoaded', initHeaderTransparency);

// Global utility functions for sticky header detection
window.HeaderSticky = {
  headerGroup: null,
  enabled: false,
  isCurrentlyStuck: false,

  // Initialize sticky header system
  init: function() {
    this.headerGroup = document.querySelector('#header-group');
    this.enabled = this.headerGroup && this.headerGroup.classList.contains('sticky-header');
    this.isCurrentlyStuck = false;
  },

  // Check if sticky header is enabled
  isEnabled: function() {
    return this.enabled;
  },

  // Handle scroll with hysteresis to prevent flickering
  handleScroll: function(scrollY) {
    if (!this.enabled || !this.headerGroup) return;

    // Use different thresholds to prevent flickering
    // Stuck when scrolling down past 100px
    // Unstuck when scrolling up past 60px (40px buffer)
    if (!this.isCurrentlyStuck && scrollY > 100) {
      this.setStuck();
    } else if (this.isCurrentlyStuck && scrollY <= 60) {
      this.setUnstuck();
    }
  },

  // Set header as stuck (fade out announcement/breadcrumbs)
  setStuck: function() {
    if (this.enabled && this.headerGroup && !this.isCurrentlyStuck) {
      this.headerGroup.classList.add('header-stuck');
      this.isCurrentlyStuck = true;
    }
  },

  // Set header as unstuck (show all sections)
  setUnstuck: function() {
    if (this.enabled && this.headerGroup && this.isCurrentlyStuck) {
      this.headerGroup.classList.remove('header-stuck');
      this.isCurrentlyStuck = false;
    }
  }
};

// Initialize sticky header detection
document.addEventListener('DOMContentLoaded', () => window.HeaderSticky.init());

// Re-initialize on screen resize (with debouncing)
let headerResizeTimeout;
window.addEventListener('resize', () => {
  clearTimeout(headerResizeTimeout);
  headerResizeTimeout = setTimeout(initHeaderTransparency, 150);
});


